import { cloneElement, isValidElement, useEffect } from 'react';
import { Box, Icon, Link, Popover, Text } from '@nimbus-ds/components';
import { CloseIcon } from '@nimbus-ds/icons';
import { useNavegate } from 'App/hooks';
import useStorage from 'commons/hooks/useStorage';
import { ORDERS_SECTION_ROUTES } from 'domains/Orders/Orders/ordersSectionRoutes';
import { trackingOrderListAutoCancelButtonClick } from 'domains/Orders/tracking';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import './OrdersAutoCancelPopover.scss';
import {
  useGetNuvemPagoFromProviderList,
  useInstalledPaymentProviders,
} from 'domains/Payments/PaymentProviders/hooks';

interface OrdersAutoCancelPopoverProps {
  showPopover?: boolean;
  children: React.ReactNode;
}

export function OrdersAutoCancelPopover({
  showPopover = true,
  children,
}: Readonly<OrdersAutoCancelPopoverProps>) {
  const [show, setShow, loaded] = useStorage(
    'orders-auto-cancel-new-feature-popover',
    true,
  );
  const t = useTranslationOrders('autoCancel');
  const { goTo } = useNavegate();

  const { paymentProviders, fetchInstalledPaymentProviders } =
    useInstalledPaymentProviders();
  const { nuvemPagoIsActive } =
    useGetNuvemPagoFromProviderList(paymentProviders);

  useEffect(() => {
    if (!paymentProviders) {
      fetchInstalledPaymentProviders();
    }
  }, [paymentProviders, fetchInstalledPaymentProviders]);

  const handleOnClose = () => {
    setShow(false);
  };

  const handleOnLinkClick = () => {
    setShow(false);
    trackingOrderListAutoCancelButtonClick(nuvemPagoIsActive ?? false);
    goTo(ORDERS_SECTION_ROUTES.autoCancel);
  };

  useEffect(() => {
    setShow(showPopover && show);
  }, [showPopover, setShow, show]);

  // Clone the children and wrap their onClick to also dismiss the popover
  const wrappedChildren =
    isValidElement(children) && typeof children.type !== 'string'
      ? cloneElement(children as React.ReactElement<any>, {
          onClick: (event: React.MouseEvent) => {
            // Call the original onClick if it exists
            if (children.props.onClick) {
              children.props.onClick(event);
            }
            // Dismiss the popover
            setShow(false);
          },
        })
      : children;

  return (
    <Popover
      position="left-start"
      visible={loaded && show}
      width="280px"
      backgroundColor="primary-interactiveHover"
      color="neutral-background"
      content={
        <div className="stratus--orders-autocancel-popover">
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            alignItems="flex-start"
            gap="4"
          >
            <Box
              display="flex"
              justifyContent="space-between"
              flexDirection="column"
              gap="2"
            >
              <Box display="flex" alignItems="flex-start">
                <Text
                  fontSize="base"
                  fontWeight="medium"
                  color="neutral-background"
                >
                  {t('popover.title')}
                </Text>
                <Icon
                  source={<CloseIcon />}
                  color="neutral-background"
                  onClick={handleOnClose}
                  cursor="pointer"
                />
              </Box>
              <Text
                fontSize="base"
                fontWeight="regular"
                color="neutral-background"
              >
                {t('popover.subtitle')}
              </Text>
            </Box>
            <Link as={Link} onClick={handleOnLinkClick}>
              {t('activateNow')}
            </Link>
          </Box>
        </div>
      }
    >
      {wrappedChildren}
    </Popover>
  );
}
